<div class="page-layout">
  <app-toast></app-toast>
  <app-sidebar></app-sidebar>

  <div class="main-area" #scrollContainer>
    <app-navbar
      pageTitle="Prerequisites"
      pageSubtitle="Upload a company organogram and supporting business documents."
    ></app-navbar>

    <div class="content">
      <div class="tabs">
        <button type="button" class="tab" [class.active]="activeTab() === 'organogram'" (click)="setTab('organogram')">Organogram</button>
        <button type="button" class="tab" [class.active]="activeTab() === 'permissions'" (click)="setTab('permissions')">
          Permissions
          <span class="tab-red-dot" *ngIf="hasUnassignedPermissions()" title="Unassigned permissions need a role"></span>
        </button>
        <button type="button" class="tab" [class.active]="activeTab() === 'business-documents'" (click)="setTab('business-documents')">Business Documents</button>
      </div>

      <!-- ORGANOGRAM -->
      <ng-container *ngIf="activeTab() === 'organogram'">
        <div class="panel">
          <div class="panel-head">
            <div>
              <p class="panel-title">Upload organogram</p>
              <p class="panel-desc">Upload an image or PDF of your org chart to extract its roles for review.</p>
            </div>
            <span
              class="status-badge"
              [ngClass]="badgeClass(organogramUpload()!.status)"
              *ngIf="organogramUpload() as u"
            >{{ u.status }}</span>
          </div>

          <div class="dropzone" *ngIf="!isUploadingOrganogram()">
            <div class="dz-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 16V4M12 4l-4 4M12 4l4 4"/><path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/></svg>
            </div>
            <div class="dz-title">{{ organogramUpload() ? 'Replace with a new organogram' : 'Choose an organogram file' }}</div>
            <div class="dz-sub">PNG, JPG or PDF</div>
            <label class="btn primary">
              {{ organogramUpload() ? 'Upload New' : 'Choose File' }}
              <input type="file" accept=".png,.jpg,.jpeg,.pdf" hidden (change)="onOrganogramFileSelected($event)" />
            </label>
          </div>

          <div class="proc-row" *ngIf="isUploadingOrganogram()">
            <div class="proc-ring"></div>
            <div>
              <div class="proc-text">Extracting roles from your organogram…</div>
              <div class="proc-sub">This can take a moment. Please don't navigate away.</div>
            </div>
          </div>
        </div>

        <div class="panel table-panel">
          <div class="panel-head-simple row">
            <div>
              <p class="panel-title">Extracted roles</p>
              <p class="panel-desc" *ngIf="organogramUpload()?.status === 'Completed'">Review each before moving on.</p>
            </div>
            <button type="button" class="btn ghost small" *ngIf="organogramUpload()?.status === 'Completed'" (click)="startAddRole()">+ Add Role</button>
          </div>

          <div class="stat-row" *ngIf="organogramUpload()?.status === 'Completed' && roleStats().total > 0">
            <div class="stat-pill"><span class="n">{{ roleStats().total }}</span><span class="l">Roles</span></div>
            <div class="stat-pill ok"><span class="n">{{ roleStats().accepted }}</span><span class="l">Accepted</span></div>
            <div class="stat-pill warn"><span class="n">{{ roleStats().pending }}</span><span class="l">Pending</span></div>
            <div class="stat-pill fail"><span class="n">{{ roleStats().rejected }}</span><span class="l">Rejected</span></div>
          </div>

          <div class="note" *ngIf="isLoadingOrganogram()">Loading…</div>
          <div class="note" *ngIf="!isLoadingOrganogram() && !organogramUpload()">
            No organogram has been uploaded yet. Once you upload one, its extracted roles will appear here for review.
          </div>
          <ng-container *ngIf="organogramUpload() as u">
            <div class="note note-row" *ngIf="u.status === 'Failed'">
              <span>{{ u.errorMessage || 'Role extraction failed for this organogram.' }}</span>
              <button type="button" class="btn ghost small" [disabled]="retryingUploadId() === u.id" (click)="retryUpload(u)">
                {{ retryingUploadId() === u.id ? 'Retrying…' : 'Retry' }}
              </button>
            </div>
          </ng-container>

          <div class="org-chart" *ngIf="organogramUpload()?.status === 'Completed'">
            <p class="empty-tagline" *ngIf="organogramUpload()!.roles.length === 0 && !isAddingRole()">
              No roles yet — none were extracted, or they've all been removed. Use "+ Add Role" to add one by hand.
            </p>

            <div class="lane-list" *ngIf="roleLevels().length > 0 || isAddingRole()">
              <div class="lane-row" *ngFor="let group of roleLevels(); trackBy: trackByLevel">
                <div class="lane-label">
                  <span class="lane-dot" [ngClass]="levelColorClass(group.level)"></span>
                  <span class="lane-label-text">Level {{ group.level }}</span>
                </div>
                <div class="lane-cards">
                  <ng-container *ngFor="let role of group.roles; trackBy: trackByRoleId">
                    <div class="org-card" [ngClass]="levelColorClass(group.level)" *ngIf="editingRoleId() !== role.id; else editOrgCard">
                      <div class="org-role">{{ role.role }}</div>
                      <div class="org-desc">{{ role.description }}</div>
                      <span class="status-badge" [ngClass]="reviewBadgeClass(role.review)">{{ reviewLabel(role.review) }}</span>
                      <div class="row-actions">
                        <button type="button" class="icon-btn accept" title="Accept" (click)="setRoleReview(role, true)">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        </button>
                        <button type="button" class="icon-btn reject" title="Reject" (click)="setRoleReview(role, false)">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                        <button type="button" class="icon-btn" title="Edit" (click)="startEditRole(role)">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                        </button>
                        <button type="button" class="icon-btn danger" title="Delete" (click)="confirmDeleteRole(role)">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </div>
                    <ng-template #editOrgCard>
                      <div class="org-card org-card-edit">
                        <input class="ff-input" placeholder="Role" [ngModel]="roleForm().role" (ngModelChange)="roleForm.set({ ...roleForm(), role: $event })" />
                        <textarea class="ff-input" rows="2" placeholder="Description" [ngModel]="roleForm().description" (ngModelChange)="roleForm.set({ ...roleForm(), description: $event })"></textarea>
                        <input class="ff-input" type="number" placeholder="Level" [ngModel]="roleForm().level" (ngModelChange)="roleForm.set({ ...roleForm(), level: $event })" />

                        <div class="perm-manage">
                          <div class="perm-manage-head">
                            <span class="perm-select-label">Permissions</span>
                            <span class="perm-manage-count" *ngIf="roleForm().permissions.length > 0">{{ roleForm().permissions.length }} selected</span>
                          </div>
                          <div class="perm-chips-compact" *ngIf="roleForm().permissions.length > 0; else noPermsSelected">
                            <span class="perm-chip-sm" *ngFor="let code of roleForm().permissions.slice(0, 3)">{{ permissionLabel(code) }}</span>
                            <span class="perm-chip-more" *ngIf="roleForm().permissions.length > 3">+{{ roleForm().permissions.length - 3 }} more</span>
                          </div>
                          <ng-template #noPermsSelected>
                            <p class="perm-hint">No permissions selected yet.</p>
                          </ng-template>
                          <button
                            type="button"
                            class="btn ghost small perm-manage-btn"
                            [disabled]="acceptedPermissions().length === 0"
                            [title]="acceptedPermissions().length === 0 ? 'No accepted permissions in the catalog yet. Add and accept some on the Permissions tab.' : ''"
                            (click)="openPermissionsOverlay()">
                            Manage Permissions
                          </button>
                        </div>

                        <div class="row-actions">
                          <button type="button" class="btn primary small" (click)="saveEditRole(role.id)">Save</button>
                          <button type="button" class="btn ghost small" (click)="cancelRoleForm()">Cancel</button>
                        </div>
                      </div>
                    </ng-template>
                  </ng-container>
                </div>
              </div>

              <div class="lane-row" *ngIf="isAddingRole()">
                <div class="lane-label">
                  <span class="lane-label-text">New</span>
                </div>
                <div class="lane-cards">
                  <div class="org-card org-card-edit">
                    <input class="ff-input" placeholder="Role" [ngModel]="roleForm().role" (ngModelChange)="roleForm.set({ ...roleForm(), role: $event })" />
                    <textarea class="ff-input" rows="2" placeholder="Description" [ngModel]="roleForm().description" (ngModelChange)="roleForm.set({ ...roleForm(), description: $event })"></textarea>
                    <input class="ff-input" type="number" placeholder="Level" [ngModel]="roleForm().level" (ngModelChange)="roleForm.set({ ...roleForm(), level: $event })" />

                    <div class="perm-manage">
                      <div class="perm-manage-head">
                        <span class="perm-select-label">Permissions</span>
                        <span class="perm-manage-count" *ngIf="roleForm().permissions.length > 0">{{ roleForm().permissions.length }} selected</span>
                      </div>
                      <div class="perm-chips-compact" *ngIf="roleForm().permissions.length > 0; else noPermsSelectedNew">
                        <span class="perm-chip-sm" *ngFor="let code of roleForm().permissions.slice(0, 3)">{{ permissionLabel(code) }}</span>
                        <span class="perm-chip-more" *ngIf="roleForm().permissions.length > 3">+{{ roleForm().permissions.length - 3 }} more</span>
                      </div>
                      <ng-template #noPermsSelectedNew>
                        <p class="perm-hint">No permissions selected yet.</p>
                      </ng-template>
                      <button
                        type="button"
                        class="btn ghost small perm-manage-btn"
                        [disabled]="acceptedPermissions().length === 0"
                        [title]="acceptedPermissions().length === 0 ? 'No accepted permissions in the catalog yet. Add and accept some on the Permissions tab.' : ''"
                        (click)="openPermissionsOverlay()">
                        Manage Permissions
                      </button>
                    </div>

                    <div class="row-actions">
                      <button type="button" class="btn primary small" (click)="saveNewRole()">Add</button>
                      <button type="button" class="btn ghost small" (click)="cancelRoleForm()">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Permissions selection overlay: shared by the edit and add-role cards above -->
          <div class="perm-overlay-backdrop" *ngIf="showPermissionsOverlay()" (click)="closePermissionsOverlay()" (document:keydown.escape)="closePermissionsOverlay()">
            <div class="perm-overlay" (click)="$event.stopPropagation()">
              <div class="perm-overlay-header">
                <div>
                  <p class="perm-overlay-title">Manage permissions</p>
                  <p class="perm-overlay-subtitle">{{ roleForm().role || 'New role' }}</p>
                </div>
                <button type="button" class="icon-btn" title="Close" (click)="closePermissionsOverlay()">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>

              <div class="perm-overlay-search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#68728F" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input type="text" placeholder="Search permissions…" [ngModel]="permissionOverlaySearch()" (ngModelChange)="permissionOverlaySearch.set($event)" />
              </div>

              <div class="perm-overlay-body">
                <div class="perm-overlay-section">
                  <div class="perm-overlay-section-head">
                    <span>Selected</span>
                    <span class="perm-overlay-count">{{ selectedOverlayPermissions().length }}</span>
                  </div>
                  <div class="perm-overlay-list" *ngIf="selectedOverlayPermissions().length > 0; else noSelectedPerms">
                    <button type="button" class="perm-overlay-item is-selected" *ngFor="let p of selectedOverlayPermissions(); trackBy: trackByPermissionId" (click)="toggleRolePermission(p.code)">
                      <span class="perm-overlay-item-check">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                      </span>
                      <span class="perm-overlay-item-text">
                        <span class="perm-overlay-item-name">{{ p.displayName }}</span>
                        <span class="perm-overlay-item-code">{{ p.code }}</span>
                      </span>
                      <span class="perm-overlay-item-action remove">Remove</span>
                    </button>
                  </div>
                  <ng-template #noSelectedPerms>
                    <p class="perm-overlay-empty">No permissions selected yet — add some from the list below.</p>
                  </ng-template>
                </div>

                <div class="perm-overlay-section">
                  <div class="perm-overlay-section-head">
                    <span>Available</span>
                    <span class="perm-overlay-count">{{ availableOverlayPermissions().length }}</span>
                  </div>
                  <div class="perm-overlay-list" *ngIf="availableOverlayPermissions().length > 0; else noAvailablePerms">
                    <button type="button" class="perm-overlay-item" *ngFor="let p of availableOverlayPermissions(); trackBy: trackByPermissionId" (click)="toggleRolePermission(p.code)">
                      <span class="perm-overlay-item-check empty"></span>
                      <span class="perm-overlay-item-text">
                        <span class="perm-overlay-item-name">{{ p.displayName }}</span>
                        <span class="perm-overlay-item-code">{{ p.code }}</span>
                      </span>
                      <span class="perm-overlay-item-action add">Add</span>
                    </button>
                  </div>
                  <ng-template #noAvailablePerms>
                    <p class="perm-overlay-empty">{{ acceptedPermissions().length === 0 ? 'No accepted permissions in the catalog yet.' : 'No matches for your search.' }}</p>
                  </ng-template>
                </div>
              </div>

              <div class="perm-overlay-footer">
                <span class="perm-overlay-summary">{{ roleForm().permissions.length }} permission(s) selected</span>
                <button type="button" class="btn primary small" (click)="closePermissionsOverlay()">Done</button>
              </div>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- PERMISSIONS CATALOG -->
      <ng-container *ngIf="activeTab() === 'permissions'">
        <div class="panel table-panel">
          <div class="panel-head-simple row">
            <div>
              <p class="panel-title">Permission catalog</p>
              <p class="panel-desc">Curated codes mapped from documents. A red dot marks permissions not yet assigned to any role.</p>
            </div>
            <div class="row gap">
              <button type="button" class="btn ghost small" [disabled]="!!permissionBusy()" (click)="startAddPermission()">+ Add Permission</button>
            </div>
          </div>

          <div class="perm-busy" *ngIf="permissionBusy() || isLoadingPermissions()">
            <div class="proc-ring"></div>
            <div>
              <div class="proc-text">{{ permissionBusy() || 'Loading permissions…' }}</div>
              <div class="proc-sub">This screen stays blocked until the action finishes.</div>
            </div>
          </div>

          <div class="perm-work" [class.is-blocked]="!!permissionBusy()">
          <div class="stat-row" *ngIf="permissionStats().total > 0">
            <div class="stat-pill"><span class="n">{{ permissionStats().total }}</span><span class="l">Permissions</span></div>
            <div class="stat-pill ok"><span class="n">{{ permissionStats().accepted }}</span><span class="l">Accepted</span></div>
            <div class="stat-pill warn"><span class="n">{{ permissionStats().pending }}</span><span class="l">Pending</span></div>
            <div class="stat-pill fail"><span class="n">{{ permissionStats().rejected }}</span><span class="l">Rejected</span></div>
            <div class="stat-pill warn" *ngIf="unassignedPermissionCount() > 0">
              <span class="n">{{ unassignedPermissionCount() }}</span><span class="l">Unassigned</span>
            </div>
          </div>

          <div class="bulk-toolbar" *ngIf="permissions().length > 0">
            <label class="bulk-check">
              <input type="checkbox" [checked]="allPermissionsSelected()" (change)="toggleSelectAllPermissions()" [disabled]="!!permissionBusy()" />
              Select all
            </label>
            <span class="bulk-count" *ngIf="selectedPermissionCount() > 0">{{ selectedPermissionCount() }} selected</span>
            <button type="button" class="btn ghost small" [disabled]="selectedPermissionCount() === 0 || !!permissionBusy()" (click)="bulkAcceptSelected()">Accept</button>
            <button type="button" class="btn ghost small" [disabled]="selectedPermissionCount() === 0 || !!permissionBusy()" (click)="bulkRejectSelected()">Reject</button>
            <button type="button" class="btn ghost small" [disabled]="selectedPermissionCount() === 0 || !!permissionBusy()" (click)="confirmBulkDeleteSelected()">Delete</button>
          </div>

          <div class="perm-filters" *ngIf="!isLoadingPermissions()">
            <input class="ff-input" placeholder="Search permissions…" [ngModel]="permissionQuery()" (ngModelChange)="permissionQuery.set($event)" />
            <button type="button" class="btn ghost small" [class.active]="permissionStatusFilter() === 'all'" (click)="permissionStatusFilter.set('all')">All</button>
            <button type="button" class="btn ghost small" [class.active]="permissionStatusFilter() === 'pending'" (click)="permissionStatusFilter.set('pending')">Pending</button>
            <button type="button" class="btn ghost small" [class.active]="permissionStatusFilter() === 'accepted'" (click)="permissionStatusFilter.set('accepted')">Accepted</button>
            <button type="button" class="btn ghost small" [class.active]="permissionStatusFilter() === 'rejected'" (click)="permissionStatusFilter.set('rejected')">Rejected</button>
          </div>

          <div class="note" *ngIf="!isLoadingPermissions() && permissions().length === 0 && !isAddingPermission()">
            No permissions yet. Add one manually, or upload an organogram.
          </div>
          <div class="note" *ngIf="!isLoadingPermissions() && permissions().length > 0 && filteredPermissions().length === 0">
            No permissions match this search or filter.
          </div>

          <div class="perm-table" *ngIf="!isLoadingPermissions()">
            <div class="perm-row perm-row-edit" *ngIf="isAddingPermission()">
              <input class="ff-input" placeholder="Display name" [ngModel]="permissionForm().displayName" (ngModelChange)="permissionForm.set({ ...permissionForm(), displayName: $event })" />
              <input class="ff-input" placeholder="Code (optional, auto-slugified)" [ngModel]="permissionForm().code" (ngModelChange)="permissionForm.set({ ...permissionForm(), code: $event })" />
              <select class="ff-input" [ngModel]="permissionForm().category" (ngModelChange)="permissionForm.set({ ...permissionForm(), category: $event })">
                <option *ngFor="let c of permissionCategories" [value]="c">{{ c }}</option>
              </select>
              <textarea class="ff-input" rows="2" placeholder="Description" [ngModel]="permissionForm().description" (ngModelChange)="permissionForm.set({ ...permissionForm(), description: $event })"></textarea>
              <input class="ff-input" placeholder="Aliases (comma-separated)" [ngModel]="permissionForm().aliasesText" (ngModelChange)="permissionForm.set({ ...permissionForm(), aliasesText: $event })" />
              <div class="row-actions">
                <button type="button" class="btn primary small" (click)="saveNewPermission()">Add</button>
                <button type="button" class="btn ghost small" (click)="cancelPermissionForm()">Cancel</button>
              </div>
            </div>

            <ng-container *ngFor="let group of permissionGroups()">
              <div class="perm-category">{{ group.category }}</div>
              <ng-container *ngFor="let item of group.items; trackBy: trackByPermissionId">
              <div class="perm-row" [class.perm-row-unassigned]="isPermissionUnassigned(item)" *ngIf="editingPermissionId() !== item.id; else editPermRow">
                <label class="bulk-check perm-check">
                  <input type="checkbox" [checked]="isPermissionSelected(item.id)" (change)="togglePermissionSelected(item.id)" />
                </label>
                <div class="perm-main">
                  <div class="perm-title">
                    <span class="row-red-dot" *ngIf="isPermissionUnassigned(item)" title="Not assigned to any organogram role"></span>
                    {{ item.displayName }}
                  </div>
                  <div class="perm-meta">
                    <span class="perm-code">{{ item.code }}</span>
                    <span *ngIf="item.category"> · {{ item.category }}</span>
                    · {{ item.source }}
                    <span class="perm-unassigned-label" *ngIf="isPermissionUnassigned(item)"> · unassigned</span>
                  </div>
                  <div class="org-desc" *ngIf="item.description">{{ item.description }}</div>
                  <div class="perm-chips" *ngIf="(item.aliases?.length ?? 0) > 0">
                    <span class="perm-chip" *ngFor="let a of item.aliases">{{ a }}</span>
                  </div>
                </div>
                <span class="status-badge" [ngClass]="reviewBadgeClass(item.review)">{{ reviewLabel(item.review) }}</span>
                <div class="row-actions">
                  <button type="button" class="icon-btn accept" title="Accept" (click)="setPermissionReview(item, true)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                  <button type="button" class="icon-btn reject" title="Reject" (click)="setPermissionReview(item, false)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                  <button type="button" class="icon-btn" title="Edit" (click)="startEditPermission(item)">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                  </button>
                  <button type="button" class="icon-btn danger" title="Delete" (click)="confirmDeletePermission(item)">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </button>
                </div>
              </div>
              <ng-template #editPermRow>
                <div class="perm-row perm-row-edit">
                  <input class="ff-input" placeholder="Display name" [ngModel]="permissionForm().displayName" (ngModelChange)="permissionForm.set({ ...permissionForm(), displayName: $event })" />
                  <input class="ff-input" placeholder="Code" [ngModel]="permissionForm().code" (ngModelChange)="permissionForm.set({ ...permissionForm(), code: $event })" />
                  <select class="ff-input" [ngModel]="permissionForm().category" (ngModelChange)="permissionForm.set({ ...permissionForm(), category: $event })">
                    <option *ngFor="let c of permissionCategories" [value]="c">{{ c }}</option>
                  </select>
                  <textarea class="ff-input" rows="2" placeholder="Description" [ngModel]="permissionForm().description" (ngModelChange)="permissionForm.set({ ...permissionForm(), description: $event })"></textarea>
                  <input class="ff-input" placeholder="Aliases (comma-separated)" [ngModel]="permissionForm().aliasesText" (ngModelChange)="permissionForm.set({ ...permissionForm(), aliasesText: $event })" />
                  <div class="row-actions">
                    <button type="button" class="btn primary small" (click)="saveEditPermission(item.id)">Save</button>
                    <button type="button" class="btn ghost small" (click)="cancelPermissionForm()">Cancel</button>
                  </div>
                </div>
              </ng-template>
            </ng-container>
            </ng-container>
          </div>
          </div>
        </div>
      </ng-container>

      <!-- BUSINESS DOCUMENTS -->
      <ng-container *ngIf="activeTab() === 'business-documents'">
        <div class="panel">
          <div class="panel-head">
            <div>
              <p class="panel-title">Upload business documents</p>
              <p class="panel-desc">Upload your business documents to extract their classifications for review.</p>
            </div>
          </div>

          <div class="dropzone" *ngIf="!isUploadingBusinessDocuments()">
            <div class="dz-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 16V4M12 4l-4 4M12 4l4 4"/><path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/></svg>
            </div>
            <div class="dz-title">Choose business documents</div>
            <div class="dz-sub">PDF, DOCX, XLSX and more &middot; select multiple, or click again to add more</div>
            <label class="btn primary">
              {{ selectedBusinessFiles().length > 0 ? 'Add More Files' : 'Choose Files' }}
              <input type="file" multiple hidden (change)="onBusinessDocumentsSelected($event)" />
            </label>
          </div>

          <div class="staged-files" *ngIf="!isUploadingBusinessDocuments() && selectedBusinessFiles().length > 0">
            <div class="staged-file" *ngFor="let file of selectedBusinessFiles(); let i = index">
              <div class="file-icon small">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
              </div>
              <div class="file-info">
                <div class="file-name">{{ file.name }}</div>
                <div class="file-meta plain">{{ formatBytes(file.size) }}</div>
              </div>
              <button type="button" class="icon-btn danger" title="Remove" (click)="removeSelectedBusinessFile(i)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div class="staged-files-footer">
              <button type="button" class="btn ghost small" (click)="clearSelectedBusinessFiles()">Clear</button>
              <button type="button" class="btn primary small" (click)="proceedBusinessUpload()">
                Upload {{ selectedBusinessFiles().length }} File{{ selectedBusinessFiles().length === 1 ? '' : 's' }}
              </button>
            </div>
          </div>

          <div class="proc-row" *ngIf="isUploadingBusinessDocuments()">
            <div class="proc-ring"></div>
            <div>
              <div class="proc-text">Uploading and classifying {{ uploadingCount() }} document(s)…</div>
              <div class="proc-sub">This can take a moment, especially for larger files.</div>
            </div>
          </div>
        </div>

        <div class="note" *ngIf="isLoadingBusinessDocuments()">Loading…</div>
        <div class="note" *ngIf="!isLoadingBusinessDocuments() && businessDocuments().length === 0">
          No business documents uploaded yet.
        </div>

        <div class="stat-row" *ngIf="businessDocuments().length > 0 && classificationStats().total > 0" style="margin-bottom:16px;">
          <div class="stat-pill"><span class="n">{{ businessDocuments().length }}</span><span class="l">Documents</span></div>
          <div class="stat-pill"><span class="n">{{ classificationStats().total }}</span><span class="l">Classifications</span></div>
          <div class="stat-pill ok"><span class="n">{{ classificationStats().accepted }}</span><span class="l">Accepted</span></div>
          <div class="stat-pill warn"><span class="n">{{ classificationStats().pending }}</span><span class="l">Pending</span></div>
          <div class="stat-pill fail"><span class="n">{{ classificationStats().rejected }}</span><span class="l">Rejected</span></div>
        </div>

        <div class="doc-toc-layout">
          <nav class="doc-toc">
            <div class="doc-toc-heading">Documents</div>
            <button
              type="button"
              class="doc-toc-item"
              *ngFor="let doc of businessDocuments()"
              [class.active]="activeDocId() === doc.id"
              (click)="scrollToDocument(doc.id)"
            >
              <span class="doc-toc-badge" [ngClass]="fileTypeInfo(doc.fileName).cssClass">{{ fileTypeInfo(doc.fileName).label }}</span>
              <span class="doc-toc-name">{{ doc.fileName }}</span>
            </button>
          </nav>

          <div class="doc-content">
            <div class="doc-content-heading">Classifications</div>
            <section class="doc-section" *ngFor="let doc of businessDocuments()" #docSection [attr.data-doc-id]="doc.id">
              <div class="doc-section-head">
                <div class="file-badge" [ngClass]="fileTypeInfo(doc.fileName).cssClass">{{ fileTypeInfo(doc.fileName).label }}</div>
                <div class="file-info">
                  <div class="file-name">{{ doc.fileName }}</div>
                  <div class="file-meta plain">{{ formatBytes(doc.fileSizeInBytes) }} &middot; {{ doc.classifications.length }} classification(s)</div>
                </div>
                <span class="status-badge" [ngClass]="badgeClass(doc.status)">{{ doc.status }}</span>
              </div>

              <div class="note note-row" *ngIf="doc.status === 'Failed'">
                <span>{{ doc.errorMessage || 'Classification failed for this document.' }}</span>
                <button type="button" class="btn ghost small" [disabled]="retryingUploadId() === doc.id" (click)="retryUpload(doc)">
                  {{ retryingUploadId() === doc.id ? 'Retrying…' : 'Retry' }}
                </button>
              </div>

              <ng-container *ngIf="doc.status === 'Completed'">
                <p class="empty-tagline" *ngIf="doc.classifications.length === 0 && addingClassificationForUploadId() !== doc.id">
                  No classifications yet — none were returned, or they've all been removed.
                </p>

                <div class="classification-row" *ngFor="let cls of doc.classifications">
                  <ng-container *ngIf="editingClassificationId() !== cls.id; else editClsForm">
                    <div class="classification-main">
                      <span class="classification-chip">{{ cls.classification }}</span>
                      <span class="status-badge" [ngClass]="reviewBadgeClass(cls.review)">{{ reviewLabel(cls.review) }}</span>
                    </div>
                    <div class="field-label evi-label">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9.5 2a5.5 5.5 0 0 0-3 10.1V14a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1v-1.9A5.5 5.5 0 0 0 9.5 2Z"/><path d="M8 21h3M9.5 17.5V21"/></svg>
                      Why this matched
                    </div>
                    <div class="evidence-block">
                      <p class="classification-evidence">{{ cls.evidenceText }}</p>
                    </div>
                    <ng-container *ngIf="cls.referenceText">
                      <div class="field-label ref-label">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"/></svg>
                        Quoted from document
                      </div>
                      <div class="reference-block">
                        <p class="classification-reference">&ldquo;{{ cls.referenceText }}&rdquo;</p>
                      </div>
                    </ng-container>
                    <div class="row-actions">
                      <button type="button" class="icon-btn accept" title="Accept" (click)="setClassificationReview(doc.id, cls, true)">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      </button>
                      <button type="button" class="icon-btn reject" title="Reject" (click)="setClassificationReview(doc.id, cls, false)">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                      <button type="button" class="icon-btn" title="Edit" (click)="startEditClassification(cls)">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                      </button>
                      <button type="button" class="icon-btn danger" title="Delete" (click)="confirmDeleteClassification(doc.id, cls)">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  </ng-container>
                  <ng-template #editClsForm>
                    <div class="inline-form stacked">
                      <input class="ff-input" placeholder="Classification" [ngModel]="classificationForm().classification" (ngModelChange)="classificationForm.set({ ...classificationForm(), classification: $event })" />
                      <textarea class="ff-input" rows="2" placeholder="Evidence" [ngModel]="classificationForm().evidenceText" (ngModelChange)="classificationForm.set({ ...classificationForm(), evidenceText: $event })"></textarea>
                      <input class="ff-input" placeholder="Reference text (optional)" [ngModel]="classificationForm().referenceText" (ngModelChange)="classificationForm.set({ ...classificationForm(), referenceText: $event })" />
                      <div class="row-actions">
                        <button type="button" class="btn primary small" (click)="saveEditClassification(doc.id, cls.id)">Save</button>
                        <button type="button" class="btn ghost small" (click)="cancelClassificationForm()">Cancel</button>
                      </div>
                    </div>
                  </ng-template>
                </div>

                <div class="classification-row" *ngIf="addingClassificationForUploadId() === doc.id">
                  <div class="inline-form stacked">
                    <input class="ff-input" placeholder="Classification" [ngModel]="classificationForm().classification" (ngModelChange)="classificationForm.set({ ...classificationForm(), classification: $event })" />
                    <textarea class="ff-input" rows="2" placeholder="Evidence" [ngModel]="classificationForm().evidenceText" (ngModelChange)="classificationForm.set({ ...classificationForm(), evidenceText: $event })"></textarea>
                    <input class="ff-input" placeholder="Reference text (optional)" [ngModel]="classificationForm().referenceText" (ngModelChange)="classificationForm.set({ ...classificationForm(), referenceText: $event })" />
                    <div class="row-actions">
                      <button type="button" class="btn primary small" (click)="saveNewClassification(doc.id)">Add</button>
                      <button type="button" class="btn ghost small" (click)="cancelClassificationForm()">Cancel</button>
                    </div>
                  </div>
                </div>

                <button type="button" class="add-link" *ngIf="addingClassificationForUploadId() !== doc.id" (click)="startAddClassification(doc.id)">+ Add classification</button>
              </ng-container>
            </section>
          </div>
        </div>
      </ng-container>
    </div>
  </div>

  <app-delete-batch-modal
    *ngIf="deleteTarget() as target"
    title="Delete confirmation"
    [message]="'Are you sure you want to delete ' + target.label + '? This cannot be undone.'"
    [isDeleting]="isDeleting()"
    (cancel)="cancelDelete()"
    (confirm)="confirmDelete()"
  ></app-delete-batch-modal>
</div>
